import React, { useEffect, useState } from 'react';
import { Mail, MapPin, Phone, Send, ArrowRight } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';

const C = {
  bgDark: '#0B5867',   // Deep Teal
  bgMid: '#2B6873',   // Mid Teal
  bgLight: '#9FBCC0',   // Mist
  accent: '#FF8C7A',   // Coral
  success: '#4ade80',   // Green
  danger: '#ef4444',   // Red
  white: '#FFFFFF',
  textSecondary: '#E2E8F0',
  glass: 'rgba(255, 255, 255, 0.08)',
  border: 'rgba(255, 255, 255, 0.15)',
};

const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=Inter:wght@300;400;600&display=swap');

  body { 
    margin: 0;
    font-family: 'Inter', sans-serif; 
    overflow-x: hidden;
  }

  .main-bg {
    background: 
      radial-gradient(circle at 50% 50%, ${C.bgLight} 0%, transparent 70%),
      linear-gradient(180deg, ${C.bgDark} 0%, ${C.bgMid} 45%, ${C.bgMid} 55%, ${C.bgDark} 100%);
    background-attachment: fixed;
    min-height: 100vh;
  }


  .glass-input {
    width: 100%;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid ${C.border};
    border-radius: 12px;
    padding: 16px 20px;
    color: ${C.white};
    font-family: 'Inter', sans-serif;
    font-size: 1rem;
    box-sizing: border-box;
    transition: all 0.3s ease;
  }
  .glass-input:focus {
    outline: none;
    border-color: ${C.accent};
    background: rgba(255, 255, 255, 0.06);
  }
  .glass-input::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const ContactUs = () => {
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = GLOBAL_STYLES;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitted(true);
      setFormData({ name: '', email: '', company: '', message: '' });
    }, 1500);
  };

  return (
    <div className="main-bg" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />

      <main style={{ flex: 1, padding: '160px 30px 100px', maxWidth: '1200px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
        
        <div style={{ textAlign: 'center', marginBottom: '80px' }}>
          <span style={{ color: C.accent, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', fontSize: '0.85rem' }}>GET IN TOUCH</span>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(3rem, 6vw, 4.5rem)', color: C.white, margin: '20px 0', lineHeight: 1.1 }}>
            Initiate a Dialogue.
          </h1>
          <p style={{ color: C.white, opacity: 0.7, fontSize: '1.2rem', maxWidth: '600px', margin: '0 auto' }}>
            Whether you're looking for capital allocation strategies or technical partnership, our team is ready to assist.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '60px' }}>
          
          {/* Contact Information */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
            <div style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${C.border}`, padding: '40px', borderRadius: '32px' }}>
              <h3 style={{ fontSize: '1.5rem', color: C.white, marginBottom: '30px', fontFamily: "'Playfair Display', serif" }}>Global Offices</h3>
              
              <div style={{ display: 'flex', gap: '20px', marginBottom: '30px' }}>
                <div style={{ color: C.accent }}><MapPin size={24} /></div>
                <div>
                  <h4 style={{ color: C.white, margin: '0 0 8px', fontSize: '1.1rem' }}>KHAMMAN (HQ)</h4>
                  <p style={{ color: C.white, opacity: 0.6, margin: 0, lineHeight: 1.6 }}>15-2-7/5, Khanapuram <br/>khammam, Pincode 507002</p>
                </div>
              </div>

              {/* <div style={{ display: 'flex', gap: '20px', marginBottom: '30px' }}>
                <div style={{ color: C.accent }}><MapPin size={24} /></div>
                <div>
                  <h4 style={{ color: C.white, margin: '0 0 8px', fontSize: '1.1rem' }}>Singapore</h4>
                  <p style={{ color: C.white, opacity: 0.6, margin: 0, lineHeight: 1.6 }}>Marina Bay Financial Centre<br/>Tower 1, Singapore 018981</p>
                </div>
              </div> */}

              <hr style={{ border: 0, borderTop: `1px solid ${C.border}`, margin: '40px 0' }} />

              <h3 style={{ fontSize: '1.5rem', color: C.white, marginBottom: '30px', fontFamily: "'Playfair Display', serif" }}>Direct Reach</h3>

              <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', alignItems: 'center' }}>
                <div style={{ color: C.accent }}><Mail size={20} /></div>
                <span style={{ color: C.white, opacity: 0.8 }}>kkbwealth@gmail.com</span>
              </div>

              <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                <div style={{ color: C.accent }}><Phone size={20} /></div>
                <span style={{ color: C.white, opacity: 0.8 }}>+91 8008197761</span>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${C.border}`, padding: '50px 40px', borderRadius: '32px' }}>
            <h3 style={{ fontSize: '1.8rem', color: C.white, marginBottom: '10px', fontFamily: "'Playfair Display', serif" }}>Send a Message</h3>
            <p style={{ color: C.white, opacity: 0.6, marginBottom: '40px' }}>We typically respond within 24 hours.</p>

            {submitted ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ width: 60, height: 60, background: C.success, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                  <Send size={30} color={C.bgDark} />
                </div>
                <h4 style={{ color: C.white, fontSize: '1.5rem', marginBottom: '10px' }}>Message Received</h4>
                <p style={{ color: C.white, opacity: 0.7 }}>Our strategy team will review your inquiry and get back to you shortly.</p>
                <button 
                  onClick={() => setSubmitted(false)}
                  style={{ marginTop: '30px', background: 'transparent', border: `1px solid ${C.accent}`, color: C.accent, padding: '10px 24px', borderRadius: '20px', cursor: 'pointer' }}
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <input required type="text" placeholder="Full Name" className="glass-input" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <input required type="email" placeholder="Email Address" className="glass-input" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
                  <input type="text" placeholder="Company" className="glass-input" value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} />
                </div>
                <div>
                  <textarea required placeholder="How can we collaborate?" className="glass-input" style={{ minHeight: '150px', resize: 'vertical' }} value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})}></textarea>
                </div>
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  style={{
                    background: C.accent, color: C.bgDark, border: 'none', padding: '18px', borderRadius: '12px',
                    fontWeight: 700, fontSize: '1.1rem', cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                    transition: 'opacity 0.3s ease', opacity: isSubmitting ? 0.7 : 1, marginTop: '10px'
                  }}
                >
                  {isSubmitting ? 'Sending...' : 'Submit Inquiry'} <ArrowRight size={20} />
                </button>
              </form>
            )}
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactUs;
